import React, { useState } from "react";
import {
  Box,
  Grid,
  Stack,
  Typography,
  Tooltip,
} from "@mui/material";
import "./FileList.css"; // import CSS file for styling
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import ConfirmDeleteSeparationDialog from "./Dialogs/ConfirmDeleteSeparationDialog";
import { useSelector } from 'react-redux'
import { SeparationStatus } from "const";
import { GetBlobUrlForFile, GetFileNameFromPath, GetFileName } from "utils";

const SeparationListItem = ({
  sourceKey,
  separationKey,
  onDeleteClick,
  onItemClick,
  isSelected,
}) => {
  const fileList = useSelector((state) => state.audioFiles);
  const file = fileList[sourceKey];
  const separation = file?.separations?.[separationKey];

  if (!separation) return null;

  const isPending = separation.status === SeparationStatus.PENDING;

  const onDelete = (event) => {
    event.stopPropagation();
    if (!isPending) {
      onDeleteClick(separationKey, separation);
    }
  };

  const onDownloadInternal = async (event) => {
    event.stopPropagation();
    if (separation.zipKey) {
      try {
        const blobUrl = await GetBlobUrlForFile(separation.zipKey);
        const filename = GetFileNameFromPath(separation.zipKey);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = filename;
        a.style.display = "none";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
      } catch (error) {
        console.error("Error fetching file:", error);
      }
    }
  };

  const onClickInternal = () => {
    if (!isPending) {
      const filteredStems = separation.stems.filter(
        (stem) => !stem.key.endsWith(".zip")
      );
      onItemClick(separation, filteredStems);
    }
  };

  return (
    <Box sx={{ cursor: "pointer" }}>
      <Grid
        container
        key={GetFileName(separation)}
        sx={{
          display: "flex",
          flexDirection: "row",
          backgroundColor: isSelected ? "#686060" : "#2f2f2f",
          alignItems: "center",
          p: { xs: 1, md: 2 },
          boxShadow: 1,
          borderRadius: 4,
          "&:hover": {
            backgroundColor: "#686060",
          },
        }}
        onClick={onClickInternal}
      >
        <Grid item xs={11} sx={{ display: { xs: "block", md: "flex" } }}>
          <Grid
            item
            xs={11}
            md={5}
            display={"flex"}
            flexGrow={1}
            sx={{ width: { xs: "100%" } }}
          >
            <Typography variant="body1">
              Title: {decodeURIComponent(file.metadata.title)}
            </Typography>
            {isPending && (
              <Typography
                variant="body1"
                sx={{ mx: 1, color: "warning.main" }}
              >
                ({separation.status})
              </Typography>
            )}
          </Grid>
          <Grid item xs={11} md={2} flexGrow={1} sx={{ width: { xs: "100%" } }}>
            <Typography variant="body1">Method: {separation.method}</Typography>
          </Grid>
          <Grid item xs={11} md={4} flexGrow={1} sx={{ width: { xs: "100%" } }}>
            <Typography variant="body1">Model: {separation.model}</Typography>
          </Grid>
          <Grid item xs={11} md={2} flexGrow={1} sx={{ width: { xs: "100%" } }}>
            <Typography variant="body1">
              Size:{" "}
              {separation.status === SeparationStatus.COMPLETE
                ? `${(separation.size / 1000000).toFixed(1)} Mb`
                : "Unknown"}
            </Typography>
          </Grid>
        </Grid>
        <Grid
          item
          xs={1}
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "flex-end",
            textAlign: "right",
          }}
        >
          <Grid item>
            <Tooltip title="Download stems">
              <DownloadIcon
                color="primary"
                onClick={!isPending ? onDownloadInternal : () => {}}
                sx={{
                  opacity: !isPending && separation.zipKey ? 1 : 0.4,
                }}
              />
            </Tooltip>
          </Grid>
          <Grid item>
            <Tooltip title="Delete">
              <DeleteIcon
                color="primary"
                onClick={!isPending ? onDelete : () => {}}
                sx={{
                  opacity: !isPending ? 1 : 0.4,
                }}
              />
            </Tooltip>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

const SeparationList = ({ separationList, onDelete, onItemClick }) => {
  const [open, setOpen] = useState(false);
  const [selectedSeparationKey, setSelectedSeparationKey] = useState(null);
  const [selectedSourceKey , setSelectedSourceKey ] = useState(null);

  const fileList = useSelector((state) => state.audioFiles);

  const handleDeleteClick = (separationKey) => {
    setSelectedSeparationKey(separationKey);
    const sourceKey = Object.keys(fileList).find((sourceKeyCandidate) => {
      const file = fileList[sourceKeyCandidate];
      return file?.separations?.[separationKey];
    });

    setSelectedSourceKey(sourceKey);
    setOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await onDelete(selectedSeparationKey);
      setOpen(false);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteCancel = () => {
    setOpen(false);
  };

  const handleClick = (separation, filteredStems) => {
    onItemClick(filteredStems);
  };

  const extractMethodAndModel = (key) => {
    const parts = key.split("/");
    return `${parts[parts.length - 2]}/${parts[parts.length - 1]}`;
  };

  const customSeparationSort = (a, b) => {
    const methodModelA = extractMethodAndModel(a[0]);
    const methodModelB = extractMethodAndModel(b[0]);
    return methodModelA.localeCompare(methodModelB);
  };

  const separationEntries = Object.entries(separationList || {});

  return (
    <Stack sx={{ flexGrow: 1 }}>
      <Stack spacing={3}>
        {separationEntries.sort(customSeparationSort).map(([separationKey, _separation]) => {
          // Look up the sourceKey by reverse-mapping the file list
          const sourceKey = Object.keys(fileList).find((sourceKeyCandidate) => {
            const file = fileList[sourceKeyCandidate];
            return file?.separations?.[separationKey];
          });

          if (!sourceKey) return null;

          return (
            <SeparationListItem
              key={separationKey}
              separationKey={separationKey}
              sourceKey={sourceKey}
              onDeleteClick={handleDeleteClick}
              onItemClick={handleClick}
              isSelected={selectedSeparationKey === separationKey}
            />
          );
        })}
  
        <ConfirmDeleteSeparationDialog
          open={open}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          separation={ selectedSourceKey ? fileList[selectedSourceKey]?.separations?.[selectedSeparationKey] ?? null : null }
        />
      </Stack>
    </Stack>
  );
};

export default SeparationList;
